import { useQuery } from "@tanstack/react-query";
import { AlertCircle, SearchX } from "lucide-react";
import type { DashboardFilter, ResultEnvelope, SemanticQueryInput, Visualization } from "@cd/core";
import { vizSpec, type Highlight } from "@cd/viz";
import { api, ApiError, type QueryOrigin } from "../../lib/api";
import { Chart } from "./Chart";
import { Kpi } from "./Kpi";
import type { OnPick } from "./onPick";
import { Pivot } from "./Pivot";
import { DataTable } from "./Table";

export function useWidgetQuery(query: SemanticQueryInput | null, locationIds?: string[], origin?: QueryOrigin, dashboardFilters?: DashboardFilter[]) {
  return useQuery({
    queryKey: ["query", query, locationIds, dashboardFilters ?? []],
    queryFn: () => api.query(query!, locationIds, origin, dashboardFilters),
    enabled: !!query && query.metrics.length > 0,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
  });
}

/** `highlight` dims the grouping values it doesn't list (a page filter on a field the widget is grouped by). */
export function VizBody({ viz, result, onPick, highlight }: { viz: Visualization; result: ResultEnvelope; onPick?: OnPick; highlight?: Highlight[] }) {
  const shape = {
    dimensions: result.columns.filter((c) => c.kind === "dimension"),
    metrics: result.columns.filter((c) => c.kind === "metric").length,
    nonAdditive: result.columns.some((c) => c.kind === "metric" && c.additive !== true),
  };
  const reason = vizSpec(viz.type).check(shape);
  if (reason) return <Empty icon={AlertCircle} text={reason} />;
  if (!result.rows.length) return <Empty icon={SearchX} text="No data for this selection." />;
  switch (viz.type) {
    case "kpi": return <Kpi viz={viz} result={result} />;
    case "table": return <DataTable viz={viz} result={result} onPick={onPick} highlight={highlight} />;
    case "pivot": return <Pivot viz={viz} result={result} onPick={onPick} highlight={highlight} />;
    default: return <Chart viz={viz} result={result} onPick={onPick} highlight={highlight} />;
  }
}

export function WidgetState({ query, error, isLoading }: { query: ReturnType<typeof useWidgetQuery>; error?: unknown; isLoading?: boolean }) {
  const e = error ?? query.error;
  if (e) return <Empty icon={AlertCircle} text={e instanceof Error ? e.message : "Couldn't load this widget."} tone="bad" />;
  if (isLoading ?? query.isLoading) return <LoadingViz />;
  return null;
}

export function LoadingViz() {
  return (
    <div className="flex size-full flex-col justify-end gap-2 p-1">
      <div className="flex h-full items-end gap-2">
        {[40, 65, 50, 80, 55, 70, 45, 90].map((h, i) => (
          <div key={i} className="skeleton flex-1" style={{ height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}

export function Empty({ icon: Icon, text, tone }: { icon: typeof AlertCircle; text: string; tone?: "bad" }) {
  return (
    <div className="flex size-full flex-col items-center justify-center gap-2 px-6 text-center">
      <Icon className={`size-5 ${tone === "bad" ? "text-bad" : "text-subtle"}`} aria-hidden />
      <p className={`max-w-sm text-sm ${tone === "bad" ? "text-bad" : "text-muted"}`}>{text}</p>
    </div>
  );
}
